import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SupplierBillService } from '../../services/supplierBill.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { GoodsReceiptService } from '../../services/goodsReceipt.service';
import { AuthService } from '../../services/auth.service';
import { PurchaseOrder } from '../../interfaces/procurement/purchaseOrder.interface';
import { GoodsReceiptLine } from '../../interfaces/procurement/goodsReceipt.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import SearchableSelect from '../common/SearchableSelect';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import { hasTaxCodes, previewTax, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { formatFc, isForeignCurrency, toastSaved } from '../../utils/fcDocuments'; // G5a

interface GRNLineWithGRN {
  grnLine: GoodsReceiptLine;
  grnId: string;
  grnNumber: string;
  /** G5a: the booking rate of the receipt (foreign PO only). */
  grnRate?: number;
}

const today = () => toLocalIsoDate();
const todayPlus30 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return toLocalIsoDate(d);
};

export const SupplierBillCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const lockedPoId = searchParams.get('purchaseOrderId');

  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [grnLines, setGrnLines] = useState<GRNLineWithGRN[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());
  // Supplier-invoiced unit price per GRN line; defaults to the GRN/PO price.
  const [lineUnitPrices, setLineUnitPrices] = useState<Record<string, number>>({});
  // Tax code per GRN line id (null/absent = uncoded).
  const [lineTaxCodes, setLineTaxCodes] = useState<Record<string, string | null>>({});
  const taxLookup = useTaxCodeLookup(AuthService.getSelectedInstitution());
  // ---- G5a: a foreign PO bills in the PO currency, takes no tax, and its AP rate defaults to the GRN rate ----
  const foreign = isForeignCurrency(selectedPO?.currencyCode);
  const lcLinked = !!selectedPO?.backToBackLcId;
  const [billRate, setBillRate] = useState<number | ''>('');
  // ---- end G5a ----
  const showTaxCodes = hasTaxCodes(taxLookup) && !foreign;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState('');
  const [billDate, setBillDate] = useState(today());
  const [dueDate, setDueDate] = useState(todayPlus30());
  // null = untouched: the field shows the server's PO-prorated tax and nothing is sent, so the
  // server computes the same figure. A typed value overrides it.
  const [taxOverride, setTaxOverride] = useState<string | null>(null);

  // Prorated tax preview -- mirrors SupplierBillService.resolveTaxAmount: PO tax x bill subtotal /
  // PO subtotal, where the bill subtotal is received qty x the BILL unit price of the selected lines.
  // G5a: on a foreign PO the bill prices are in the PO currency: the default is the PO foreign price on the GRN line
  const defaultPrice = (l: GoodsReceiptLine) => (foreign ? (l.foreignUnitPrice ?? 0) : l.unitPrice);
  const billSubtotal = useMemo(() => {
    return grnLines
      .filter(l => selectedLineIds.has(l.grnLine.id))
      .reduce((sum, l) => {
        const price = lineUnitPrices[l.grnLine.id] ?? defaultPrice(l.grnLine);
        return sum + (Number.isFinite(price) ? price : 0) * l.grnLine.receivedQty;
      }, 0);
  }, [grnLines, selectedLineIds, lineUnitPrices, foreign]); // eslint-disable-line react-hooks/exhaustive-deps

  // G5a: the server's default AP rate: the one rate the selected receipts share, else the table rate on the bill date
  const sharedGrnRate = useMemo(() => {
    const rates = new Set(grnLines.filter(l => selectedLineIds.has(l.grnLine.id)).map(l => l.grnRate));
    return rates.size === 1 ? Array.from(rates)[0] : undefined;
  }, [grnLines, selectedLineIds]);

  const proratedTax = useMemo(() => {
    if (!selectedPO || !selectedPO.subtotal) return 0;
    return Math.round((billSubtotal / selectedPO.subtotal) * selectedPO.taxAmount * 100) / 100;
  }, [billSubtotal, selectedPO]);

  useEffect(() => {
    if (!lockedPoId) {
      // Load selectable POs: ORDERED, PARTIALLY_RECEIVED, RECEIVED, CLOSED
      Promise.all([
        PurchaseOrderService.listByInstitution(0, 500, 'ORDERED'),
        PurchaseOrderService.listByInstitution(0, 500, 'PARTIALLY_RECEIVED'),
        PurchaseOrderService.listByInstitution(0, 500, 'RECEIVED'),
        PurchaseOrderService.listByInstitution(0, 500, 'CLOSED'),
      ])
        .then(([o, p, r, c]) => setPos([...o.content, ...p.content, ...r.content, ...c.content]))
        .catch(e => toast.showApiError(e, 'Failed to load purchase orders'))
        .finally(() => setLoading(false));
    } else {
      PurchaseOrderService.getById(lockedPoId)
        .then(po => {
          setSelectedPO(po);
          return loadGRNLines(po.id);
        })
        .catch(e => toast.showApiError(e, 'Failed to load data'))
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGRNLines = async (poId: string) => {
    const grnRes = await GoodsReceiptService.listByInstitution(0, 500);
    const postedSummaries = grnRes.content.filter(g => g.purchaseOrderId === poId && g.status === 'POSTED');
    // The list endpoint omits nested lines; fetch each GRN's detail to get them.
    const postedGRNs = await Promise.all(postedSummaries.map(g => GoodsReceiptService.getById(g.id)));
    const lines: GRNLineWithGRN[] = postedGRNs.flatMap(g =>
      (g.lines ?? []).map(l => ({ grnLine: l, grnId: g.id, grnNumber: g.grnNumber, grnRate: g.exchangeRate }))
    );
    setGrnLines(lines);
    return lines;
  };

  const handlePOChange = async (poId: string) => {
    const po = pos.find(p => p.id === poId);
    if (!po) return;
    setSelectedPO(po);
    setGrnLines([]);
    setSelectedLineIds(new Set());
    // An override typed for the previous PO is stale against this one's tax.
    setTaxOverride(null);
    setLineUnitPrices({});
    setBillRate('');
    setLoading(true);
    try {
      await loadGRNLines(po.id);
    } catch (e) {
      toast.showApiError(e, 'Failed to load GRN lines');
    } finally {
      setLoading(false);
    }
  };

  const rateOf = (codeId: string | null | undefined) =>
    (codeId ? taxLookup?.codes?.find(c => c.id === codeId)?.effectiveRatePct : undefined) ?? 0;
  const anyCoded = grnLines.some(l => selectedLineIds.has(l.grnLine.id) && !!lineTaxCodes[l.grnLine.id]);

  const toggleLine = (lineId: string) => {
    // First time a line is ticked, prefill the institution's default input code (if one is configured).
    const dcId = taxLookup?.defaultInputCodeId;
    const dc = dcId ? taxLookup?.codes?.find(c => c.id === dcId && c.active) : undefined;
    if (dc && !selectedLineIds.has(lineId) && !(lineId in lineTaxCodes)) {
      setLineTaxCodes(prev => ({ ...prev, [lineId]: dc.id }));
    }
    setSelectedLineIds(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!selectedPO) { toast.showApiError(null, 'Select a Purchase Order'); return; }
    if (lcLinked) { toast.showApiError(null, 'This purchase order is settled through its B2B LC acceptance: it cannot be billed'); return; }
    if (selectedLineIds.size === 0) { toast.showApiError(null, 'Select at least one GRN line'); return; }

    const selectedLines = grnLines.filter(l => selectedLineIds.has(l.grnLine.id));
    const priceOf = (l: GRNLineWithGRN) => lineUnitPrices[l.grnLine.id] ?? defaultPrice(l.grnLine);
    if (selectedLines.some(l => !(priceOf(l) > 0))) {
      toast.showApiError(null, 'Enter a positive bill unit price for every selected line');
      return;
    }

    setSaving(true);
    try {
      const dto = {
        purchaseOrderId: selectedPO.id,
        supplierInvoiceRef: supplierInvoiceRef || undefined,
        billDate,
        dueDate,
        // With any coded line the backend sums the line taxes and ignores the header tax.
        taxAmount: !foreign && !anyCoded && taxOverride !== null && taxOverride !== '' ? Number(taxOverride) : undefined,
        institutionId: AuthService.getSelectedInstitution()!!,
        lines: selectedLines.map(l => ({
          goodsReceiptLineId: l.grnLine.id,
          quantity: l.grnLine.receivedQty,
          unitPrice: lineUnitPrices[l.grnLine.id] ?? defaultPrice(l.grnLine),
          taxCodeId: foreign ? null : (lineTaxCodes[l.grnLine.id] || null),
        })),
        ...(foreign ? { currencyCode: selectedPO.currencyCode, ...(Number(billRate) > 0 ? { exchangeRate: Number(billRate) } : {}) } : {}),
      };
      const bill = await SupplierBillService.create(dto);
      toastSaved(toast, 'Supplier Bill created', bill.warnings);
      navigate(`/procurement/supplier-bills/${bill.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create Supplier Bill');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.billForm.page">
      <Typography variant="h5" sx={{ mb: 3 }}>New Supplier Bill</Typography>

      <Paper sx={{ p: 3, mb: 3 }} data-help-section="procurement.billForm.section.header">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SearchableSelect<PurchaseOrder>
            options={lockedPoId && selectedPO ? [selectedPO] : pos}
            value={selectedPO?.id ?? ''}
            onChange={id => handlePOChange(id)}
            getOptionLabel={po => isForeignCurrency(po.currencyCode) ? `${po.poNumber} (${po.currencyCode})` : po.poNumber}
            data-help="procurement.billForm.field.po"
            label="Purchase Order"
            size="medium"
            disabled={!!lockedPoId}
          />

          <TextField
            data-help="procurement.billForm.field.invoiceRef"
            label="Supplier Invoice Ref"
            value={supplierInvoiceRef}
            onChange={e => setSupplierInvoiceRef(e.target.value)}
          />

          <TextField
            data-help="procurement.billForm.field.billDate"
            label="Bill Date"
            type="date"
            value={billDate}
            onChange={e => setBillDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            data-help="procurement.billForm.field.dueDate"
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          {/* ---- G5a ---- */}
          {lcLinked && (
            <Alert severity="warning">
              {selectedPO?.poNumber} is linked to B2B LC {selectedPO?.backToBackLcNumber ?? ''}: it is settled through the LC acceptance and cannot be billed here.
            </Alert>
          )}
          {foreign && selectedPO?.currencyCode && (
            <TextField
              data-help="procurement.billForm.field.rate"
              label={`Bill rate (BDT per ${selectedPO.currencyCode})`}
              type="number"
              value={billRate}
              onChange={e => setBillRate(e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ min: 0, step: 'any' }}
              helperText={sharedGrnRate
                ? `Blank: the receipts' rate ${sharedGrnRate}. A different rate posts the difference to FX gain / loss.`
                : 'Blank: the exchange-rate table rate on the bill date. The gap to the receipt rates posts to FX gain / loss.'}
            />
          )}
          {/* ---- end G5a ---- */}
          {!foreign && (
          <Box>
            <TextField
              data-help="procurement.billForm.field.tax"
              label="Tax Amount"
              type="number"
              value={taxOverride ?? (selectedLineIds.size > 0 ? proratedTax : 0)}
              onChange={e => setTaxOverride(e.target.value)}
              inputProps={{ step: 0.01, min: 0 }}
              disabled={anyCoded}
              helperText={anyCoded ? 'Tax is calculated from the line tax codes' : undefined}
            />
            {selectedPO && selectedLineIds.size > 0 && !anyCoded && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {taxOverride === null
                  ? `Auto: ${formatMoney(proratedTax)} is the selected lines' share of the purchase order's tax of ${formatMoney(selectedPO.taxAmount)}. Type a value to override.`
                  : `Overriding the computed ${formatMoney(proratedTax)} (share of the purchase order's tax of ${formatMoney(selectedPO.taxAmount)}). A large difference will hold the bill for review.`}
                {taxOverride !== null && (
                  <Button data-help="procurement.billForm.action.useComputed" size="small" onClick={() => setTaxOverride(null)} sx={{ ml: 1 }}>Use computed</Button>
                )}
              </Typography>
            )}
          </Box>
          )}
          {foreign && (
            <Typography variant="caption" color="text.secondary">
              No tax on a foreign-currency PO bill: import VAT, AT and AIT come through the bill of entry.
            </Typography>
          )}
        </Box>
      </Paper>

      {selectedPO && (
        <Paper sx={{ p: 3, mb: 3 }} data-help-section="procurement.billForm.lines">
          <Typography variant="h6" sx={{ mb: 2 }}>Received goods to bill</Typography>
          {grnLines.length === 0 ? (
            <Typography color="text.secondary">No posted goods receipts found for this purchase order.</Typography>
          ) : (
            <ScrollableTable>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell data-help="procurement.billForm.col.select" padding="checkbox">Select</TableCell>
                      <TableCell data-help="procurement.billForm.col.grn">GRN Number</TableCell>
                      <TableCell data-help="procurement.billForm.col.item">Item</TableCell>
                      <TableCell data-help="procurement.billForm.col.qty">Received Qty</TableCell>
                      <TableCell data-help="procurement.billForm.col.poPrice">PO Unit Price{foreign ? ` (${selectedPO.currencyCode})` : ''}</TableCell>
                      <TableCell data-help="procurement.billForm.col.billPrice">Bill Unit Price{foreign ? ` (${selectedPO.currencyCode})` : ''}</TableCell>
                      {showTaxCodes && <TableCell data-help="procurement.billForm.col.taxCode">Tax code</TableCell>}
                      <TableCell data-help="procurement.billForm.col.value">Line Value</TableCell>
                      {showTaxCodes && <TableCell data-help="procurement.billForm.col.tax">Tax</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grnLines.map(({ grnLine, grnNumber }) => {
                      const selected = selectedLineIds.has(grnLine.id);
                      const rawPrice = lineUnitPrices[grnLine.id] ?? defaultPrice(grnLine);
                      const billPrice = Number.isFinite(rawPrice) ? rawPrice : NaN;
                      return (
                      <TableRow key={grnLine.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            data-help="procurement.billForm.field.lineSelect"
                            checked={selected}
                            onChange={() => toggleLine(grnLine.id)}
                          />
                        </TableCell>
                        <TableCell>{grnNumber}</TableCell>
                        <TableCell>{grnLine.itemName ?? '—'}{grnLine.itemCode ? ` (${grnLine.itemCode})` : ''}</TableCell>
                        <TableCell>{grnLine.receivedQty}{grnLine.unit ? ` ${grnLine.unit}` : ''}</TableCell>
                        <TableCell>{foreign ? (grnLine.foreignUnitPrice ?? '—') : formatMoney(grnLine.unitPrice)}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            data-help="procurement.billForm.field.linePrice"
                            error={selected && !(billPrice > 0)}
                            value={Number.isFinite(billPrice) ? billPrice : ''}
                            disabled={!selected}
                            onChange={e => {
                              const v = e.target.value;
                              setLineUnitPrices(prev => ({
                                ...prev,
                                [grnLine.id]: v === '' ? NaN : Number(v),
                              }));
                            }}
                            inputProps={{ step: 'any', min: 0, 'aria-label': `bill-unit-price-${grnLine.id}` }}
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        {showTaxCodes && (
                          <TableCell data-help="procurement.billForm.field.lineTaxCode">
                            <TaxCodeSelect
                              data-testid={`bill-taxcode-${grnLine.id}`}
                              lookup={taxLookup}
                              side="INPUT"
                              value={lineTaxCodes[grnLine.id]}
                              disabled={!selected}
                              onChange={id => setLineTaxCodes(prev => ({ ...prev, [grnLine.id]: id }))}
                            />
                          </TableCell>
                        )}
                        <TableCell>{Number.isFinite(billPrice)
                          ? (foreign && selectedPO.currencyCode ? formatFc(selectedPO.currencyCode, billPrice * grnLine.receivedQty) : formatMoney(billPrice * grnLine.receivedQty))
                          : '—'}</TableCell>
                        {showTaxCodes && (
                          <TableCell>
                            {selected && lineTaxCodes[grnLine.id] && Number.isFinite(billPrice)
                              ? formatMoney(previewTax(billPrice * grnLine.receivedQty, rateOf(lineTaxCodes[grnLine.id]))) : '—'}
                          </TableCell>
                        )}
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
            </ScrollableTable>
          )}
          {foreign && selectedPO.currencyCode && selectedLineIds.size > 0 && (
            <Typography sx={{ mt: 2, textAlign: 'right' }}>
              Bill total: {formatFc(selectedPO.currencyCode, billSubtotal)}
              {(Number(billRate) > 0 || sharedGrnRate) ? ` ≈ ${formatMoney(billSubtotal * (Number(billRate) > 0 ? Number(billRate) : (sharedGrnRate as number)))}` : ''}
            </Typography>
          )}
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button data-help="procurement.billForm.action.create" variant="contained" onClick={handleSubmit} disabled={saving || lcLinked}>
          {saving ? <CircularProgress size={20} /> : 'Create Bill'}
        </Button>
        <Button data-help="procurement.billForm.cancel" variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SupplierBillCreate;
